import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { AuthService } from '../../_services/auth.service';
import { SiteReqDto } from '../../_dtos/siteReqDto';
import { SitesService } from '../../_services/sites.service';
import { SharepointFormData } from '../../_models/sharepointFormData';
import { CommonModule } from '@angular/common';
import { SharedService } from '../../_services/shared.service';
import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { ValidatorService } from '../../_services/validator.service';
import { BaseMetadata } from '../../_models/baseMetadata';
import { DepartmentResDto } from '../../_dtos/departmentResDto';
import { CategoryResDto } from '../../_dtos/categoryResDto';
import { TranslateModule } from '@ngx-translate/core';
import { DepartmentsService } from '../../_services/departments.service';
import { TranslatedToastService } from '../../_services/translated-toast.service';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { UserResDto } from '../../_dtos/userResDto';
import { GraphService } from '../../_services/graph.service';
import { CategoriesService } from '../../_services/categories.service';

@Component({
  selector: 'app-order-form-standard-sharepoint',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatButton,
    MatError,
    CommonModule,
    MatTooltip,
    TranslateModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
  ],
  templateUrl: './order-form-standard-sharepoint.component.html',
  standalone: true,
  styleUrl: './order-form-standard-sharepoint.component.css',
})
export class OrderFormStandardSharepointComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public departments!: DepartmentResDto[];
  public department!: DepartmentResDto;
  public categories!: CategoryResDto[];
  public standardSharepointForm!: FormGroup;
  public category!: string;
  public departmentShortName!: string;
  private selectedSecondaryOwner?: UserResDto = undefined;
  secondaryOwnerControl = new FormControl('');
  filteredPeople?: Observable<UserResDto[]>;

  constructor(
    private sharepointService: SitesService,
    private authService: AuthService,
    private toastr: TranslatedToastService,
    private sharedService: SharedService,
    private validatorService: ValidatorService,
    private departmentsService: DepartmentsService,
    private graphService: GraphService,
    private categoriesService: CategoriesService,
  ) {}

  ngOnInit() {
    this.getDataFromSharedService();
    this.initializeForm();
    this.setupFormListeners();
    this.prefillForm();
    this.setupSecondaryOwnerAutocomplete();
  }

  async onSubmit() {
    if (this.standardSharepointForm.valid) {
      const formValues = this.standardSharepointForm.getRawValue();
      const sharepointMetadata: BaseMetadata = {
        department: this.departmentShortName,
      };

      const username = this.authService.getUsername();
      const sharepointRequestDto: SiteReqDto = {
        name: formValues.name,
        siteName: formValues.siteName,
        category: formValues.category,
        primaryOwner: username,
        secondaryOwner: formValues.secondaryOwner,
        createdBy: username,
        metadata: sharepointMetadata,
      };

      this.sharedService.closeModal();
      await this.toastr.show(
        'success',
        'TOASTS.SHAREPOINT.SUBMIT_MESSAGE',
        'TOASTS.SHAREPOINT.SUBMIT_TITLE',
      );
      this.sharepointService.createSharepoint(sharepointRequestDto).subscribe({
        next: async (siteRes) => {
          await this.toastr.show(
            'success',
            'TOASTS.SHAREPOINT.CREATION_SUCCESS',
            undefined,
            { siteName: sharepointRequestDto.name },
          );
          const url = siteRes.url;
          const urlToast = await this.toastr.show(
            'info',
            'TOASTS.SHAREPOINT.URL_MESSAGE',
            'TOASTS.SHAREPOINT.URL_TITLE',
            { url: url },
            {
              timeOut: 0,
              closeButton: true,
              enableHtml: true,
              tapToDismiss: false,
              extendedTimeOut: 0,
            },
          );
          urlToast.onTap.subscribe(() => {
            window.open(url, '_blank');
          });
        },
      });
    }
  }

  get name() {
    return this.standardSharepointForm.get('name');
  }

  get errorMessage() {
    if (this.name?.hasError('required')) return 'Site name is required';
    else if (this.name?.hasError('hasForbiddenChars'))
      return 'Contains forbidden Character';
    else if (this.name?.hasError('exceedsMaxLength'))
      return 'The provided name is too long';
    else if (this.name?.hasError('siteNameTaken'))
      return 'This site name is already taken';
    else return '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm() {
    this.standardSharepointForm = new FormGroup<SharepointFormData>({
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
        asyncValidators: [
          this.validatorService.validateSharePointSiteName(
            this.department.shortName,
          ),
        ],
        updateOn: 'blur',
      }),
      siteName: new FormControl(
        { value: '', disabled: true },
        { nonNullable: true },
      ),
      category: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      primaryOwner: new FormControl(
        { value: this.authService.getDisplayName(), disabled: true },
        { nonNullable: true },
      ),
      secondaryOwner: new FormControl(undefined, { nonNullable: true }),
      department: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });

    this.secondaryOwnerControl = this.standardSharepointForm.get(
      'secondaryOwner',
    ) as FormControl;

    this.standardSharepointForm
      .get('name')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.validatorService.validateSharePointSiteName(
          this.department.shortName,
        );
      });
  }

  private getDataFromSharedService() {
    this.departments = this.departmentsService.getDepartments();
    this.categories = this.categoriesService.getSpCategories();
    this.department = this.departmentsService.getDepartment();
    const userType = this.graphService.getUserType();
    this.category = userType ? userType.toLowerCase() : 'NO CATEGORY FOUND!';
  }

  private prefillForm() {
    this.standardSharepointForm.patchValue({
      department: this.department.name,
      category: this.category,
    });
  }

  onSecondaryOwnerSelected(event: MatAutocompleteSelectedEvent) {
    this.selectedSecondaryOwner = event.option.value;
    this.standardSharepointForm.patchValue({
      secondaryOwner: this.selectedSecondaryOwner!.userPrincipalName,
    });
  }

  private setupFormListeners() {
    this.standardSharepointForm.get('name')?.valueChanges.subscribe((value) => {
      const revisedValue = this.validatorService.reviseValue(value);
      const fullSitename = this.standardSharepointForm.get('siteName')!;

      if (!fullSitename.value) {
        const newValue = `${this.departmentShortName}-${revisedValue}`;
        this.standardSharepointForm
          .get('siteName')
          ?.setValue(newValue, { emitEvent: false });
      } else {
        const parts = fullSitename.value.split('-');
        if (parts.length > 0) {
          const department = parts[0];
          const newValue = `${department}-${revisedValue}`;
          this.standardSharepointForm
            .get('siteName')
            ?.setValue(newValue, { emitEvent: false });
        }
      }
    });

    this.standardSharepointForm
      .get('department')!
      .valueChanges.subscribe((departmentName) => {
        this.departmentsService
          .getDepartmentObservable(departmentName)
          .subscribe({
            next: (data) => {
              this.departmentShortName = data.shortName;

              const fullSitename = this.standardSharepointForm.get('siteName')!;
              const parts = fullSitename.value.split('-');
              if (parts.length > 1) {
                const siteName = parts.slice(1).join('-');
                const revisedValue = `${this.departmentShortName}-${siteName}`;
                this.standardSharepointForm
                  .get('siteName')
                  ?.setValue(revisedValue, { emitEvent: false });
              }
            },
          });
      });
  }

  private setupSecondaryOwnerAutocomplete() {
    this.filteredPeople = this.secondaryOwnerControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((value) => {
        if (!value || value.trim() === '') return of([]);

        return this.graphService.searchPeople(value.trim());
      }),
    );
  }
}
